import { useState } from "react";
import { cn } from "lib/utils";
import Modal from "shared/Modal";
import Button from "shared/Button";
import { FORM_CONTROL } from "shared/tableStyles";
import type { EvaluationPayload } from "../modal/evaluate_context";
import { CRITERIA } from "./evaluationConfig";
import ScorePicker from "./ScorePicker";

interface EvaluationFormProps {
  employeeName: string;
  initial: EvaluationPayload;
  editing: boolean;
  onSave: (data: EvaluationPayload) => Promise<void>;
  onClose: () => void;
}

export default function EvaluationForm({ employeeName, initial, editing, onSave, onClose }: EvaluationFormProps) {
  const [form, setForm] = useState<EvaluationPayload>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const scoredCount = CRITERIA.filter(({ key }) => form[key] > 0).length;

  async function submit() {
    if (!form.created_at) {
      setError("Choose the evaluation date.");
      return;
    }
    // New evaluations need every criterion; older ones may legitimately carry unscored (0) criteria.
    if (!editing && scoredCount < CRITERIA.length) {
      setError("Score every criterion before saving.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSave(form);
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Unable to save evaluation.");
      setSaving(false);
    }
  }

  const close = saving ? undefined : onClose;

  return (
    <Modal
      size="lg"
      title={editing ? "Edit evaluation" : "New evaluation"}
      description={employeeName}
      onClick={close}
      closeButtonCLick={close}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={saving}>
            {saving ? "Saving…" : editing ? "Save changes" : "Create evaluation"}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="max-w-[12rem]">
          <label htmlFor="evaluation-date" className="block text-xs font-medium text-muted-foreground mb-1.5">Evaluation date</label>
          <input
            id="evaluation-date"
            type="date"
            value={form.created_at}
            onChange={(e) => setForm((p) => ({ ...p, created_at: e.target.value }))}
            className={cn(FORM_CONTROL, "w-full px-3 py-2.5")}
          />
        </div>

        <div>
          <div className="flex items-baseline justify-between mb-2">
            <p className="text-xs font-medium text-muted-foreground">
              Scores <span className="font-normal text-muted-foreground/70">(1–5, ½ for half points; click again to clear)</span>
            </p>
            <span className="text-xs text-muted-foreground">{scoredCount} of {CRITERIA.length} scored</span>
          </div>
          <div className="border border-border rounded-xl divide-y divide-border">
            {CRITERIA.map(({ key, label }) => (
              <div key={key} className="px-4 py-2.5">
                <ScorePicker label={label} value={form[key]} onChange={(v) => setForm((p) => ({ ...p, [key]: v }))} />
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">General comments</label>
            <textarea
              rows={5}
              value={form.general_comments}
              onChange={(e) => setForm((p) => ({ ...p, general_comments: e.target.value }))}
              className={cn(FORM_CONTROL, "w-full px-3 py-2.5 resize-y")}
              placeholder="Overall impression…"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Extra comments</label>
            <textarea
              rows={5}
              value={form.extra_comments}
              onChange={(e) => setForm((p) => ({ ...p, extra_comments: e.target.value }))}
              className={cn(FORM_CONTROL, "w-full px-3 py-2.5 resize-y")}
              placeholder="Anything else worth recording…"
            />
          </div>
        </div>

        {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
      </div>
    </Modal>
  );
}
